import { useState } from "react";
import type { GetThreadResp } from "@/types/thread.ts";

/**
 * Whether this rendering of a thread should offer Delete in place of its
 * archive toggle — the rule every surface that shows one standing action
 * applies, so a thread never offers one thing here and another there for a
 * reason none of them agree on.
 *
 * Shared rather than web's own because the answer has to match across the two
 * apps: the same archived thread offering Delete on web and Unarchive on iOS
 * is the failure. Only the boolean is shared; what gets drawn for it, and the
 * confirmation behind it, stay per platform.
 *
 * The guarantee is per surface, not per thread. Each caller holds its own
 * latch, so a thread archived in one place and then met afresh in another is
 * answered twice, and the second answer is the one that view arrived at.
 *
 * The rule is narrower than "is archived right now" in one way: **it latches on
 * arrival.** Promoting a control to Delete while the user is on it is the
 * hazard. Archiving patches `completed` into every list cache optimistically, so
 * a live read turns the button they just pressed destructive — for the round
 * trip in every view, and for good in the filters that keep archived rows.
 * Demotion is the opposite: free, so it happens at once, and permanent for this
 * rendering, so it cannot be undone into the hazard. Unarchiving in place hands
 * the toggle straight back, and archiving again from there keeps it. Delete
 * returns when the thread is arrived at afresh, which every caller makes a
 * remount: the list keys its rows on `thread.id`, and the detail panel keys
 * `HeaderActions` the same way.
 *
 * Origin does not narrow it. A routine run reaches `completed` by finishing
 * rather than by anyone filing it away, which is a reason to treat it as a run
 * history and not a pile to clear. It loses to the shape of a real archive,
 * which is mostly routine output: gating on the label hides the action almost
 * everywhere it is wanted, and a finished run is as much a thing to throw out as
 * any other archived thread.
 */
export const useOffersDelete = (thread: GetThreadResp): boolean => {
  const isArchived = thread.status === "completed";

  const [arrivedArchived, setArrivedArchived] = useState(() => isArchived);
  if (arrivedArchived && !isArchived) setArrivedArchived(false);

  return arrivedArchived;
};
